import { api } from './api';
import type { ApiResponse } from '../types/index';
import type {
  Payment,
  Refund,
  StoredPaymentMethod,
  Subscription,
  PaymentIntentResponse,
  UserBalance,
  CreatePaymentIntentRequest,
  RecordManualPaymentRequest,
  InitiateRefundRequest,
  CreateSubscriptionRequest,
  UpdateSubscriptionRequest,
  PaymentFilters,
} from '../types/payments';
import { buildQueryString } from '../utils/query-string';

export const paymentService = {
  // Payment Intent
  async createPaymentIntent(data: CreatePaymentIntentRequest): Promise<ApiResponse<PaymentIntentResponse>> {
    return api.post<ApiResponse<PaymentIntentResponse>>('/payments/create-intent', data);
  },

  // Manual Payments
  async recordManualPayment(data: RecordManualPaymentRequest): Promise<ApiResponse<{ payment: Payment }>> {
    return api.post<ApiResponse<{ payment: Payment }>>('/payments/manual', data);
  },

  // Payment History
  async getUserPayments(filters?: PaymentFilters): Promise<ApiResponse<{ payments: Payment[]; totalCount?: number }>> {
    const query = filters ? buildQueryString(filters as Record<string, unknown>) : '';
    return api.get<ApiResponse<{ payments: Payment[]; totalCount?: number }>>(`/payments/my-payments${query}`);
  },

  async getAllPayments(filters?: PaymentFilters): Promise<ApiResponse<{ payments: Payment[]; totalCount?: number }>> {
    const query = filters ? buildQueryString(filters as Record<string, unknown>) : '';
    return api.get<ApiResponse<{ payments: Payment[]; totalCount?: number }>>(`/payments${query}`);
  },

  // Balance
  async getUserBalance(): Promise<ApiResponse<{ balance: UserBalance }>> {
    return api.get<ApiResponse<{ balance: UserBalance }>>('/payments/balance');
  },

  // Send receipt email
  async sendReceipt(paymentId: string): Promise<ApiResponse<{ sent: boolean }>> {
    return api.post<ApiResponse<{ sent: boolean }>>(`/payments/${paymentId}/send-receipt`);
  },

  // Refunds
  async initiateRefund(paymentId: string, data: InitiateRefundRequest): Promise<ApiResponse<{ refund: Refund }>> {
    return api.post<ApiResponse<{ refund: Refund }>>(`/payments/${paymentId}/refund`, data);
  },

  // Payment Methods
  async getPaymentMethods(): Promise<ApiResponse<{ paymentMethods: StoredPaymentMethod[] }>> {
    return api.get<ApiResponse<{ paymentMethods: StoredPaymentMethod[] }>>('/payment-methods');
  },

  async deletePaymentMethod(id: string): Promise<ApiResponse<void>> {
    return api.delete<ApiResponse<void>>(`/payment-methods/${id}`);
  },

  async setDefaultPaymentMethod(id: string): Promise<ApiResponse<{ paymentMethod: StoredPaymentMethod }>> {
    return api.put<ApiResponse<{ paymentMethod: StoredPaymentMethod }>>(`/payment-methods/${id}/default`);
  },

  // Checkout Sessions
  async createCheckoutSession(data: {
    assessmentId: string;
    frequency?: 'one_time' | 'monthly' | 'quarterly' | 'annually';
    amount?: number;
  }): Promise<ApiResponse<{ checkoutUrl: string; sessionId: string }>> {
    return api.post<ApiResponse<{ checkoutUrl: string; sessionId: string }>>('/payments/checkout-session', data);
  },

  // Customer Portal
  async createPortalSession(returnUrl?: string): Promise<ApiResponse<{ portalUrl: string }>> {
    return api.post<ApiResponse<{ portalUrl: string }>>('/payments/portal-session', { returnUrl });
  },

  // Payment Links (admin)
  async createPaymentLink(data: {
    assessmentId?: string;
    amount?: number;
    description?: string;
  }): Promise<ApiResponse<{ paymentLinkUrl: string; paymentLinkId: string }>> {
    return api.post<ApiResponse<{ paymentLinkUrl: string; paymentLinkId: string }>>('/payments/payment-links', data);
  },

  // Subscriptions
  async createSubscription(data: CreateSubscriptionRequest): Promise<ApiResponse<{ subscription: Subscription }>> {
    return api.post<ApiResponse<{ subscription: Subscription }>>('/subscriptions', data);
  },

  async getMySubscription(): Promise<ApiResponse<{ subscription: Subscription | null }>> {
    return api.get<ApiResponse<{ subscription: Subscription | null }>>('/subscriptions/mine');
  },

  async cancelSubscription(id: string): Promise<ApiResponse<{ subscription: Subscription }>> {
    return api.post<ApiResponse<{ subscription: Subscription }>>(`/subscriptions/${id}/cancel`);
  },

  async updateSubscriptionFrequency(id: string, data: UpdateSubscriptionRequest): Promise<ApiResponse<{ subscription: Subscription }>> {
    return api.put<ApiResponse<{ subscription: Subscription }>>(`/subscriptions/${id}/frequency`, data);
  },
};
